// src/components/Tableros/Campo/HorizontalScoreboard.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import HorizontalEventTable from '../Campo/Tablas/HorizontalEventTable';

/**
 * Componente para mostrar el tablero horizontal en una ventana independiente
 * Carga la configuración desde localStorage y la pasa al componente de visualización
 */
const HorizontalScoreboard = () => {
  const [searchParams] = useSearchParams();
  const configId = searchParams.get('id');
  
  // Estados para almacenar configuración y datos
  const [config, setConfig] = useState(null);
  const [displayRows, setDisplayRows] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Actualizar dimensiones cuando cambia el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar la configuración desde localStorage
  useEffect(() => {
    if (!configId) {
      setError('No se proporcionó ID de configuración');
      setLoading(false);
      return;
    }

    const loadConfig = () => {
      try {
        const savedConfig = localStorage.getItem(`config-${configId}`);
        console.log('[HorizontalScoreboard] Configuración cargada:', savedConfig);
        
        if (savedConfig) {
          const parsedConfig = JSON.parse(savedConfig);
          console.log('[HorizontalScoreboard] Config parseada:', parsedConfig);
          setConfig(parsedConfig);
          
          // Usar displayRows configurado
          const configuredRows = parsedConfig.settings?.displayRows || 8;
          console.log('[HorizontalScoreboard] Filas configuradas:', configuredRows);
          setDisplayRows(configuredRows);
        } else {
          setError(`No se encontró configuración para el ID: ${configId}`);
        }
      } catch (err) {
        console.error('[HorizontalScoreboard] Error al cargar la configuración:', err);
        setError(`Error al cargar la configuración: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();

    // Escuchar mensajes para actualizar la configuración
    const handleMessage = (event) => {
      // Verificar origen por seguridad
      if (event.origin !== window.location.origin) return;
      
      console.log('[HorizontalScoreboard] Mensaje recibido:', event.data);
      
      if (event.data) {
        if (event.data.type === 'CONFIG_UPDATE' || event.data.type === 'INITIAL_CONFIG') {
          console.log('[HorizontalScoreboard] Recibida actualización de configuración:', event.data.type);
          
          if (event.data.config) {
            const newConfig = event.data.config;
            console.log('[HorizontalScoreboard] Nueva configuración recibida:', newConfig);
            console.log('[HorizontalScoreboard] EventData en nueva config:', newConfig.eventData);
            console.log('[HorizontalScoreboard] Atleta activo en config:', newConfig.eventData?.activeAthlete);
            
            setConfig(newConfig);
            
            // Actualizar localStorage
            localStorage.setItem(`config-${configId}`, JSON.stringify(newConfig));
            
            // Actualizar displayRows desde la configuración
            const configuredRows = newConfig.settings?.displayRows || 8;
            console.log('[HorizontalScoreboard] Filas re-configuradas tras mensaje:', configuredRows);
            setDisplayRows(configuredRows);
            
            console.log('[HorizontalScoreboard] Configuración actualizada exitosamente');
          }
        } else if (event.data.type === 'updateConfig') {
          // Mantener compatibilidad con el mensaje anterior
          console.log('[HorizontalScoreboard] Recibido mensaje updateConfig (legacy)');
          loadConfig();
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Notificar que el tablero está listo
    if (window.opener) {
      window.opener.postMessage({ type: 'SCOREBOARD_READY', configId }, '*');
    }
    
    // Configurar el tamaño del documento
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.body.style.backgroundColor = '#1a1a1a';
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [configId]);

  // Si está cargando, mostrar un indicador
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white'
      }}>
        <CircularProgress color="primary" sx={{ mr: 2 }} />
        <Typography>Cargando tablero horizontal...</Typography>
      </Box>
    );
  }

  // Si hay un error, mostrarlo
  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white',
        flexDirection: 'column',
        p: 3
      }}>
        <Typography variant="h6" color="error" gutterBottom>Error</Typography>
        <Typography>{error}</Typography>
      </Box>
    );
  }

  // Si no hay configuración o no hay eventData, mostrar mensaje
  if (!config || !config.eventData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        width: '100vw',
        backgroundColor: '#1a1a1a',
        color: 'white',
        flexDirection: 'column',
        gap: 2
      }}>
        <Typography variant="h6">Esperando datos del evento...</Typography>
        <Typography variant="body2" color="textSecondary">
          La configuración se ha cargado pero los datos del evento aún no están disponibles.
        </Typography>
        {config && (
          <Typography variant="caption" color="textSecondary">
            Config ID: {configId}
          </Typography>
        )}
      </Box>
    );
  }

  // Extraer las configuraciones
  const { settings, eventData } = config;
  
  // ✅ DEPURACIÓN MEJORADA: Mostrar datos del atleta activo
  console.log('[HorizontalScoreboard] === ANÁLISIS DE DATOS ===');
  console.log('[HorizontalScoreboard] EventData completo:', eventData);
  console.log('[HorizontalScoreboard] ActiveAthlete directo:', eventData.activeAthlete);
  console.log('[HorizontalScoreboard] Participantes:', eventData.participants);
  console.log('[HorizontalScoreboard] DisplayRows:', displayRows);

  // Usar el tamaño de la ventana como predeterminado
  const tableWidth = windowDimensions.width;
  const tableHeight = windowDimensions.height;

  // ✅ FUNCIÓN CORREGIDA: Procesar atletas con mejor detección del atleta activo
  const processAthletes = () => {
    if (!eventData?.participants || eventData.participants.length === 0) {
      console.warn('[HorizontalScoreboard] No hay participantes en eventData');
      return [];
    }

    console.log('[HorizontalScoreboard] Procesando participantes reales:', eventData.participants);

    // ✅ PROCESAR PARTICIPANTES CON DETECCIÓN MEJORADA DEL ATLETA ACTIVO
    return eventData.participants
      .sort((a, b) => {
        // Ordenar por posición si existe
        const posA = parseInt(a.place) || 999;
        const posB = parseInt(b.place) || 999;
        if (posA !== posB) return posA - posB;
        
        // Si las posiciones son iguales, ordenar por lane
        return (parseInt(a.lane) || 0) - (parseInt(b.lane) || 0);
      })
      .map((participant, index) => {
        console.log(`[HorizontalScoreboard] Procesando participante ${index}:`, participant);
        
        // ✅ DETECCIÓN MEJORADA: Verificar si es el atleta activo por múltiples métodos
        let isActiveAthlete = false;
        let activeAthleteData = null;
        
        // Método 1: Verificar si el participante tiene isActive
        if (participant.isActive) {
          isActiveAthlete = true;
          activeAthleteData = participant;
          console.log('[HorizontalScoreboard] Atleta activo encontrado por isActive:', participant.fullName || participant.name);
        }
        
        // Método 2: Verificar contra eventData.activeAthlete por idNumber
        else if (eventData.activeAthlete && participant.idNumber === eventData.activeAthlete.idNumber) {
          isActiveAthlete = true;
          activeAthleteData = { ...participant, ...eventData.activeAthlete };
          console.log('[HorizontalScoreboard] Atleta activo encontrado por idNumber:', activeAthleteData.fullName || activeAthleteData.name);
        }
        
        // Usar datos del atleta activo si existe, si no, datos del participante
        const athleteData = activeAthleteData || participant;
        
        // Obtener la mejor marca de los intentos o usar resultado actual
        let bestMark = athleteData.currentResult || athleteData.mark || '-';
        let bestWind = athleteData.currentWind || athleteData.wind || null;
        let currentAttemptNumber = athleteData.activeAttempt || athleteData.currentAttempt || 1;
        
        // Si no hay resultado actual, procesar intentos existentes
        if (!athleteData.currentResult && athleteData.attempts && athleteData.attempts.length > 0) {
          const validAttempts = athleteData.attempts
            .filter(att => att && att.result && !isNaN(parseFloat(att.result)))
            .map(att => ({
              mark: parseFloat(att.result),
              wind: att.wind ? parseFloat(att.wind) : null
            }));
          
          if (validAttempts.length > 0) {
            const bestAttempt = validAttempts.reduce((best, current) => 
              current.mark > best.mark ? current : best
            );
            bestMark = bestAttempt.mark.toFixed(2);
            bestWind = bestAttempt.wind;
          }
          
          currentAttemptNumber = athleteData.attempts.length;
        }

        const processedAthlete = {
          position: athleteData.place === '-' || !athleteData.place ? `${index + 1}` : athleteData.place,
          name: athleteData.fullName || athleteData.name || 'Sin nombre',
          fullName: athleteData.fullName || athleteData.name || 'Sin nombre',
          team: athleteData.affiliation || athleteData.team || '',
          affiliation: athleteData.affiliation || athleteData.team || '',
          mark: bestMark,
          wind: bestWind,
          attemptNumber: currentAttemptNumber.toString(),
          lane: athleteData.lane?.toString() || `${index + 1}`,
          number: athleteData.idNumber || `${100 + index}`,
          idNumber: athleteData.idNumber || `${100 + index}`,
          special: athleteData.special || '-',
          // ✅ DATOS ESPECÍFICOS PARA ATLETA ACTIVO CORREGIDOS
          isActive: isActiveAthlete,
          activeAttempt: athleteData.activeAttempt || athleteData.currentAttempt,
          currentAttempt: athleteData.currentAttempt || athleteData.activeAttempt,
          currentResult: athleteData.currentResult,
          currentWind: athleteData.currentWind
        };
        
        console.log(`[HorizontalScoreboard] Atleta procesado:`, {
          name: processedAthlete.name,
          isActive: processedAthlete.isActive,
          currentResult: processedAthlete.currentResult,
          activeAttempt: processedAthlete.activeAttempt
        });
        
        return processedAthlete;
      })
      .slice(0, displayRows); // Limitar al número de filas a mostrar
  };

  // Procesar lista de atletas
  const athletesList = processAthletes();
  
  console.log('[HorizontalScoreboard] Lista final de atletas procesados:', athletesList);
  console.log('[HorizontalScoreboard] Número de atletas a mostrar:', athletesList.length);
  
  // ✅ DETECCIÓN CORREGIDA DEL ATLETA ACTIVO EN LA LISTA
  const activeAthleteInList = athletesList.find(a => a.isActive);
  console.log('[HorizontalScoreboard] Atleta activo en la lista:', activeAthleteInList?.name || 'Ninguno');
  
  if (activeAthleteInList) {
    console.log('[HorizontalScoreboard] Datos del atleta activo:', {
      name: activeAthleteInList.name,
      currentResult: activeAthleteInList.currentResult,
      activeAttempt: activeAthleteInList.activeAttempt,
      isActive: activeAthleteInList.isActive
    });
  }

  // ✅ CONSTRUCCIÓN CORREGIDA DE EVENTDATA CON ATLETA ACTIVO
  const enhancedEventData = {
    eventName: eventData.eventName,
    eventSubtitle: eventData.eventSubtitle,
    participants: athletesList,
    // ✅ CORREGIDO: Usar atleta activo de la lista procesada o del eventData original
    activeAthlete: activeAthleteInList || eventData.activeAthlete || null,
    displayMode: eventData.displayMode || (displayRows === 1 ? 'individual' : 'multiple')
  };

  console.log('[HorizontalScoreboard] === EVENTDATA FINAL ===');
  console.log('[HorizontalScoreboard] EventData mejorado:', {
    eventName: enhancedEventData.eventName,
    activeAthlete: enhancedEventData.activeAthlete?.name || 'null',
    activeAthleteIsActive: enhancedEventData.activeAthlete?.isActive,
    activeAthleteResult: enhancedEventData.activeAthlete?.currentResult,
    participants: enhancedEventData.participants?.length
  });

  return (
    <Box sx={{ 
      width: '100vw', 
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: settings?.tableBackgroundColor || '#1a1a1a'
    }}>
      <HorizontalEventTable
        eventData={enhancedEventData}
        displayRows={displayRows}
        tableWidth={tableWidth}
        tableHeight={tableHeight}
        tableBackgroundColor={settings?.tableBackgroundColor || '#1a1a1a'}
        headerColor={settings?.headerBackgroundColor || '#2c0369'}
        positionColor={settings?.positionBackgroundColor || '#4c516d'}
        nameBackgroundColor={settings?.nameBackgroundColor || '#515e7b'}
        markBackgroundColor={settings?.markBackgroundColor || '#151515'}
        attemptBackgroundColor={settings?.passColor || '#0d47a1'}
        headerFontColor={settings?.headerFontColor || '#ffffff'}
        athleteNameColor={settings?.athleteNameColor || '#ffffff'}
        teamNameColor={settings?.teamNameColor || '#e0e0e0'}
        resultColor={settings?.resultTimeColor || '#ffffff'}
        attemptColor={settings?.headerFontColor || '#ffffff'}
        laneColor={settings?.laneColor || '#ffffff'}
        numberColor={settings?.numberColor || '#ffffff'}
        tableRowOddColor={settings?.tableRowOddColor || '#252525'}
        tableRowEvenColor={settings?.tableRowEvenColor || '#1e1e1e'}
        headerFontSize={settings?.headerFontSize || 24}
        positionFontSize={settings?.positionFontSize || 48}
        nameFontSize={settings?.nameFontSize || 36}
        teamFontSize={settings?.teamFontSize || 24}
        resultFontSize={settings?.resultFontSize || 54}
        attemptFontSize={settings?.attemptFontSize || 18}
        showAttemptNumber={settings?.showAttemptNumber !== undefined ? settings.showAttemptNumber : true}
        showLane={settings?.showLane !== undefined ? settings.showLane : true}
        showNumber={settings?.showNumber !== undefined ? settings.showNumber : false}
        headerFontFamily={settings?.headerFontFamily || 'Roboto'}
        nameFontFamily={settings?.nameFontFamily || 'Roboto'}
        numbersFontFamily={settings?.numbersFontFamily || 'Roboto Mono'}
      />
    </Box>
  );
};

export default HorizontalScoreboard;  